import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { useElementSize } from '../../../util';
import { Icon } from '../Icon';

// Consuming apps serve pdfjs-dist's main + worker scripts as static assets at
// these paths (see apps/advisor/scripts/copy-pdf-worker.cjs). Loading pdfjs
// via a runtime URL -- rather than a static `import 'pdfjs-dist'` -- means
// its ~1MB never enters any bundle and, critically, never gets parsed by a
// bundler at all: esbuild (the classic Remix compiler apps/advisor still
// uses) mis-downlevels pdfjs-dist's modern ES2022 static-initializer syntax
// when it tries to bundle it, crashing at module-eval time. The
// `@vite-ignore`/`webpackIgnore` comments mirror pdf.js's own internal
// fallback-worker loader, which uses this exact pattern for the same reason.
const PDFJS_MODULE_URL = '/pdf-worker/pdf.min.mjs';
const PDFJS_WORKER_URL = '/pdf-worker/pdf.worker.min.mjs';

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | undefined;

const loadPdfjs = () => {
  pdfjsPromise ??= import(/* @vite-ignore */ /* webpackIgnore: true */ PDFJS_MODULE_URL)
    .then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return pdfjs;
    })
    .catch((error: unknown) => {
      pdfjsPromise = undefined;
      throw error;
    });
  return pdfjsPromise;
};

export interface PdfDocumentPaneProps {
  downloadURL: string;
  onError: () => void;
}

export const PdfDocumentPane: FC<PdfDocumentPaneProps> = ({ downloadURL, onError }) => {
  const { t: tFile } = useTranslation('file');
  const [containerRef, { width }] = useElementSize<HTMLDivElement>();
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy>();
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    let loadedDocument: PDFDocumentProxy | undefined;
    setPdfDocument(undefined);

    void loadPdfjs()
      .then((pdfjs) => pdfjs.getDocument({ url: downloadURL, isEvalSupported: false }).promise)
      .then((document) => {
        if (cancelled) {
          void document.destroy();
          return;
        }
        loadedDocument = document;
        setPdfDocument(document);
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      void loadedDocument?.destroy();
    };
  }, [downloadURL]);

  useEffect(() => {
    if (!pdfDocument || !width) return;
    let cancelled = false;
    let activeTask: ReturnType<PDFPageProxy['render']> | undefined;

    const renderPages = async () => {
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        if (cancelled) return;
        const canvas = canvasRefs.current[pageNumber - 1];
        if (!canvas) continue;
        const page = await pdfDocument.getPage(pageNumber);
        const unscaledWidth = page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale: Math.min(width - 48, 960) / unscaledWidth });
        const context = canvas.getContext('2d');
        if (!context || cancelled) continue;
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = viewport.width * devicePixelRatio;
        canvas.height = viewport.height * devicePixelRatio;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.scale(devicePixelRatio, devicePixelRatio);
        activeTask = page.render({ canvas, canvasContext: context, viewport });
        await activeTask.promise;
        activeTask = undefined;
      }
    };

    void renderPages().catch((error: unknown) => {
      if (cancelled && (error as { name?: string } | undefined)?.name === 'RenderingCancelledException') return;
      onErrorRef.current();
    });

    return () => {
      cancelled = true;
      activeTask?.cancel();
    };
  }, [pdfDocument, width]);

  return (
    <div ref={containerRef} className="h-full w-full overflow-y-auto">
      {!pdfDocument ? (
        <div
          role="status"
          aria-label={tFile('document-viewer-loading-label')}
          className="flex h-full items-center justify-center"
        >
          <Icon icon="spinner" className="h-8 w-8 text-darkMode-gray-light" />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 py-6">
          {Array.from({ length: pdfDocument.numPages }, (_, index) => (
            <canvas
              key={index}
              ref={(el) => {
                canvasRefs.current[index] = el;
              }}
              className="shadow-lg"
            />
          ))}
        </div>
      )}
    </div>
  );
};
